import {Combobox, Input, TextInput, useCombobox} from '@mantine/core';
import {Group} from 'src/design/atoms/Group';
import {ScrollArea} from 'src/design/atoms/ScrollArea';
import {Text} from 'src/design/atoms/Text';
import {UnstyledButton} from 'src/design/atoms/UnstyledButton';
import {IconChevronDown} from '@tabler/icons-react';
import type {CountryCode} from 'libphonenumber-js';
import React, {useCallback, useMemo, useState} from 'react';
import type {IFormFieldV2} from 'src/types/FormV2';
import {
  applyPhoneNumberCountry,
  detectPhoneNumberCountry,
  formatPhoneNumberAsYouType,
  getPhoneCountry,
  DEFAULT_PHONE_COUNTRY,
  PHONE_COUNTRIES,
} from 'src/utilities/phoneUtils';

const PHONE_PLACEHOLDER = 'Example: +1 (555) 555-5555';
const COUNTRY_SECTION_WIDTH = 56;

export interface FormPhoneNumberV2Props {
  field: IFormFieldV2;
  value: string;
  error?: string;
  onChange: (name: string, value: any) => void;
}

export const FormPhoneNumberV2 = React.memo<FormPhoneNumberV2Props>(
  ({field, value, error, onChange}: FormPhoneNumberV2Props) => {
    const [pickedCountry, setPickedCountry] =
      useState<CountryCode>(DEFAULT_PHONE_COUNTRY);
    const [search, setSearch] = useState('');

    const combobox = useCombobox({
      onDropdownClose: () => {
        combobox.resetSelectedOption();
        setSearch('');
      },
    });

    const country = useMemo(
      () => detectPhoneNumberCountry(value) ?? pickedCountry,
      [value, pickedCountry]
    );

    const handleChange = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        onChange(
          field.name,
          formatPhoneNumberAsYouType(event.currentTarget.value, country)
        );
      },
      [field.name, onChange, country]
    );

    const handleCountrySubmit = useCallback(
      (nextValue: string) => {
        const nextCountry = nextValue as CountryCode;
        setPickedCountry(nextCountry);
        onChange(field.name, applyPhoneNumberCountry(value, nextCountry));
        combobox.closeDropdown();
      },
      [field.name, onChange, value, combobox]
    );

    const options = useMemo(() => {
      const query = search.trim().toLowerCase();

      return PHONE_COUNTRIES.filter(
        option =>
          query === '' ||
          option.name.toLowerCase().includes(query) ||
          option.callingCode.includes(query) ||
          option.code.toLowerCase().startsWith(query)
      ).map(option => (
        <Combobox.Option value={option.code} key={option.code}>
          <Group gap="xs" wrap="nowrap" justify="space-between">
            <Group gap="xs" wrap="nowrap">
              <span>{option.flag}</span>
              <Text size="sm">{option.name}</Text>
            </Group>
            <Text size="sm" c="dimmed">
              {option.callingCode}
            </Text>
          </Group>
        </Combobox.Option>
      ));
    }, [search]);

    return (
      <Input.Wrapper
        data-testid={`form-field-${field.name}`}
        label={field.label}
        withAsterisk={field.required}
        description={field.description}
        error={error}
        mb={'xs'}>
        <TextInput
          data-testid={`form-field-${field.name}-input`}
          type="tel"
          inputMode="tel"
          value={value || ''}
          onChange={handleChange}
          error={!!error}
          required={field.required}
          autoComplete="new-password"
          placeholder={
            field.placeholder ??
            (field.validation?.pattern ? PHONE_PLACEHOLDER : undefined)
          }
          disabled={field.disabled}
          mt={8}
          leftSectionWidth={COUNTRY_SECTION_WIDTH}
          leftSectionPointerEvents="all"
          leftSection={
            <Combobox
              store={combobox}
              width={320}
              position="bottom-start"
              withinPortal
              onOptionSubmit={handleCountrySubmit}>
              <Combobox.Target>
                <UnstyledButton
                  data-testid={`form-field-${field.name}-country`}
                  aria-label="Country calling code"
                  disabled={field.disabled}
                  onClick={() => combobox.toggleDropdown()}
                  w={COUNTRY_SECTION_WIDTH}
                  h="100%">
                  <Group gap={4} wrap="nowrap" justify="center">
                    <span>{getPhoneCountry(country)?.flag}</span>
                    <IconChevronDown size={14} />
                  </Group>
                </UnstyledButton>
              </Combobox.Target>
              <Combobox.Dropdown>
                <Combobox.Search
                  value={search}
                  onChange={event => setSearch(event.currentTarget.value)}
                  placeholder="Search country"
                />
                <Combobox.Options>
                  <ScrollArea.Autosize mah={280} type="scroll">
                    {options}
                  </ScrollArea.Autosize>
                </Combobox.Options>
              </Combobox.Dropdown>
            </Combobox>
          }
        />
      </Input.Wrapper>
    );
  }
);
